<template>
  <BottomSheet
    ref="sheet"
    fit
    close-position="end"
    title="Sort"
    data-cy="datatable-sort-sheet"
    @hide-sheet="emit('hide-sheet')"
  >
    <p
      class="px-4 py-3 text-sm text-gray-600 border-b border-gray-100"
      data-cy="datatable-sort-sheet-current"
    >
      <template v-if="currentSortSummary"
        >Sorted by {{ currentSortSummary }}</template
      >
      <template v-else>No sort applied</template>
    </p>
    <p
      v-if="columns.length === 0"
      class="px-4 py-3 text-sm text-gray-500"
      data-cy="datatable-sort-sheet-empty"
    >
      Every sortable column is visible right now, so there is nothing to sort
      from here.
    </p>
    <ul v-else class="flex flex-col divide-y divide-gray-100">
      <li
        v-for="col in columns"
        :key="col.index"
        class="flex items-center justify-between gap-4 px-4 py-3"
        :data-cy="`datatable-sort-sheet-row-${col.index}`"
      >
        <p class="text-sm font-medium text-gray-900">{{ col.label }}</p>
        <div class="flex gap-2">
          <button
            type="button"
            class="flex items-center gap-1 rounded-lg border border-solid px-2 py-1 text-xs"
            :class="
              isActiveDirection(col.index, 'asc')
                ? 'border-blue-900 text-blue-900 font-semibold'
                : 'border-gray-300 text-gray-600'
            "
            :aria-pressed="isActiveDirection(col.index, 'asc')"
            :data-cy="`datatable-sort-sheet-asc-${col.index}`"
            @click="choose(col, 'asc')"
          >
            <ChevronUpIcon class="h-3 w-3" />
            Ascending
          </button>
          <button
            type="button"
            class="flex items-center gap-1 rounded-lg border border-solid px-2 py-1 text-xs"
            :class="
              isActiveDirection(col.index, 'desc')
                ? 'border-blue-900 text-blue-900 font-semibold'
                : 'border-gray-300 text-gray-600'
            "
            :aria-pressed="isActiveDirection(col.index, 'desc')"
            :data-cy="`datatable-sort-sheet-desc-${col.index}`"
            @click="choose(col, 'desc')"
          >
            <ChevronDownIcon class="h-3 w-3" />
            Descending
          </button>
        </div>
      </li>
    </ul>
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { trackMobileWebEvent } from "@/lib/pendo";

import BottomSheet from "@/components/Seranova/BottomSheet/BottomSheet.vue";
import ChevronDownIcon from "@/images/chevron-down.svg?component";
import ChevronUpIcon from "@/images/chevron-up.svg?component";

// The sort control chosen over pinning every sortable column visible: a
// bottom sheet listing the columns Responsive has currently collapsed,
// naming the active sort, and writing a new one through the parent's own
// `sort` emit. This component owns no DataTables state itself -- it only
// renders what it is handed and reports what was tapped; DatatableV3.vue is
// the only place that writes through the table's order API, exactly as it
// is the only place that resolves a column's label outside this
// component's own row list (`currentSortLabel`).
//
// No screen-size check of its own: this sheet only ever renders below `md`
// (its trigger in DatatableV3.vue is gated the same way), so it needs no
// useBreakpoints() call and, in particular, no call to the deprecated
// `breakpoints` mixin helper the Filter button's badge block uses for its
// own `sm` check.

interface SortColumn {
  index: number;
  label: string;
  // The column's data key, for the Pendo change payload -- schema, not
  // content. null only for a column DataTables was configured without a
  // `data` key at all.
  data: string | null;
}

const props = defineProps<{
  columns: SortColumn[];
  tableId: string;
  currentSort: [number, string] | null;
  currentSortLabel: string | null;
}>();

const emit = defineEmits<{
  (e: "sort", payload: { index: number; direction: string }): void;
  (e: "hide-sheet"): void;
}>();

const sheet = ref<InstanceType<typeof BottomSheet> | null>(null);

// Names the current sort even when its column is not in `columns` (the row
// list is collapsed-sortable columns only; the sorted column may be visible).
// currentSortLabel is resolved by the parent for exactly that reason -- it is
// the only place that can name a column outside this component's own row
// list.
const currentSortSummary = computed(() => {
  if (!props.currentSort || !props.currentSortLabel) return null;
  const direction =
    props.currentSort[1] === "desc" ? "descending" : "ascending";
  return `${props.currentSortLabel} (${direction})`;
});

const isActiveDirection = (index: number, direction: "asc" | "desc") =>
  props.currentSort !== null &&
  props.currentSort[0] === index &&
  props.currentSort[1] === direction;

const choose = (col: SortColumn, direction: "asc" | "desc") => {
  emit("sort", { index: col.index, direction });
  trackMobileWebEvent("datatable_sort_change", {
    surface: "datatable",
    table_id: props.tableId,
    column: col.data,
    direction
  });
};

onMounted(() => {
  trackMobileWebEvent("datatable_sort_sheet_open", {
    surface: "datatable",
    table_id: props.tableId,
    column_count: props.columns.length
  });
});

const close = () => {
  sheet.value?.close();
};

defineExpose({ close });
</script>
